import type { ImplementedLocale } from '~/lib/i18n/shared'
import { LocalizedLink } from '~/components/common/localized-link'
import {
  getLegalDocumentContent,
  type LegalDocumentId,
  legalDocumentIds,
} from './legal-content'

export interface LegalPageProps {
  locale: ImplementedLocale
  document: LegalDocumentId
}

export function LegalPage({ locale, document }: LegalPageProps) {
  const content = getLegalDocumentContent(locale, document)

  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-10 sm:py-14 lg:px-6 lg:py-16">
      <nav
        aria-label={locale === 'zh-CN' ? '法律文件' : 'Legal documents'}
        className="mb-10 flex flex-wrap gap-x-6 gap-y-3 border-b border-divider pb-4"
      >
        {legalDocumentIds.map((item) => {
          const itemContent = getLegalDocumentContent(locale, item)
          return (
            <LocalizedLink
              key={item}
              to={`/legal/${item}`}
              aria-current={item === document ? 'page' : undefined}
              className={item === document
                ? 'inline-flex min-h-11 items-center px-1 text-sm font-bold text-foreground underline decoration-primary decoration-2 underline-offset-8'
                : 'inline-flex min-h-11 items-center px-1 text-sm font-semibold text-muted underline-offset-8 hover:text-foreground hover:underline'}
            >
              {itemContent.title}
            </LocalizedLink>
          )
        })}
      </nav>

      <article className="max-w-[72ch]">
        <header className="mb-12 border-b border-divider pb-10">
          <h1 className="text-balance text-4xl font-black tracking-[-0.03em] text-foreground sm:text-5xl">
            {content.title}
          </h1>
          <p className="mt-5 text-pretty text-lg leading-8 text-muted">
            {content.summary}
          </p>
          <p className="mt-5 text-sm font-semibold tabular-nums text-muted">
            {content.effectiveLabel}
            {': '}
            {content.effectiveDate}
          </p>
        </header>

        <div className="space-y-12">
          {content.sections.map(section => (
            <section key={section.id} id={section.id} className="scroll-mt-24">
              <h2 className="text-balance text-2xl font-bold tracking-[-0.02em] text-foreground">
                {section.title}
              </h2>
              <div className="mt-4 space-y-4 text-base leading-7 text-muted">
                {section.paragraphs.map(paragraph => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
                {section.bullets && (
                  <ul className="list-disc space-y-3 pl-5 marker:text-primary">
                    {section.bullets.map(item => (
                      <li key={item} className="pl-1">{item}</li>
                    ))}
                  </ul>
                )}
              </div>
            </section>
          ))}
        </div>
      </article>
    </div>
  )
}
